import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import ApiConfigModal from '../components/ApiConfigModal';

/* ─── Shared helpers ─────────────────────────────────────────────────────── */
const PURPLE = '#6c63ff';
const AMBER  = '#f7b731';
const GREEN  = 'var(--accent3)';   // theme-aware: neon dark / readable light
const GREEN_RAW = '#43e97b';       // only for dark code-block backgrounds

function CodeBlock({ code, lang = 'javascript' }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{
      background: '#0d0d16', border: '1px solid var(--border)',
      borderRadius: '8px', marginBottom: '1rem', overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '6px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <span style={{ fontSize: '0.7rem', color: '#6b7280', fontFamily: 'JetBrains Mono, monospace' }}>{lang}</span>
        <button onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
          style={{ background: 'none', border: 'none', color: copied ? GREEN_RAW : '#6b7280', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'JetBrains Mono, monospace' }}>
          {copied ? '✓ copied' : 'copy'}
        </button>
      </div>
      <pre style={{ margin: 0, padding: '14px 16px', overflowX: 'auto', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', color: '#e2e4f0', lineHeight: 1.65 }}>
        <code>{code}</code>
      </pre>
    </div>
  );
}

const InfoBox = ({ type = 'info', children }) => {
  const map = {
    info:    { border: PURPLE, icon: 'ℹ' },
    warning: { border: AMBER,  icon: '⚠' },
    tip:     { border: GREEN,  icon: '✓' },
    danger:  { border: '#ff6584', icon: '✕' },
  };
  const c = map[type];
  return (
    <div style={{
      borderLeft: `3px solid ${c.border}`, borderRadius: '6px',
      padding: '10px 14px', marginBottom: '1rem',
      background: 'var(--surface2)', border: `1px solid var(--border)`,
      fontSize: '0.84rem', color: 'var(--text)', lineHeight: 1.7,
    }}>
      <span style={{ color: c.border, fontWeight: 700, marginRight: '6px' }}>{c.icon}</span>
      {children}
    </div>
  );
};

const Section = ({ title, children }) => (
  <section style={{ marginBottom: '3rem' }}>
    <h2 style={{
      fontSize: '1.25rem', fontWeight: 700, color: 'var(--accent)',
      borderBottom: '2px solid var(--border)', paddingBottom: '0.5rem', marginBottom: '1.25rem',
    }}>{title}</h2>
    {children}
  </section>
);

const SubSection = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.7rem' }}>{title}</h3>
    {children}
  </div>
);

const Inline = ({ children, color }) => (
  <code style={{
    fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8em',
    color: color || 'var(--accent)', background: 'var(--surface2)',
    padding: '1px 6px', borderRadius: '4px',
  }}>{children}</code>
);

/* ─── Wireframe: Browser window ─────────────────────────────────────────── */
function BrowserFrame({ title, url, children, accentColor = PURPLE }) {
  return (
    <div style={{
      border: `2px solid ${accentColor}66`, borderRadius: '10px', overflow: 'hidden',
      background: 'var(--surface)', boxShadow: `0 4px 24px ${accentColor}18`,
      minWidth: 0,
    }}>
      {/* Title bar */}
      <div style={{
        background: 'var(--surface2)', padding: '8px 12px',
        borderBottom: `1px solid var(--border)`,
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>
        <div style={{ display: 'flex', gap: '5px' }}>
          {['#ff5f56', '#ffbd2e', '#27c93f'].map(c => (
            <div key={c} style={{ width: '10px', height: '10px', borderRadius: '50%', background: c }} />
          ))}
        </div>
        <div style={{
          flex: 1, background: 'var(--bg)', borderRadius: '4px',
          padding: '3px 10px', fontSize: '0.68rem', color: 'var(--muted)',
          fontFamily: 'JetBrains Mono, monospace', border: '1px solid var(--border)',
        }}>{url}</div>
      </div>
      {/* Content */}
      <div style={{ padding: '12px' }}>{children}</div>
      <div style={{
        textAlign: 'center', padding: '6px', fontSize: '0.68rem',
        color: accentColor, fontWeight: 700, background: accentColor + '10',
        borderTop: `1px solid ${accentColor}33`,
      }}>{title}</div>
    </div>
  );
}

/* Fake mini app layout inside the browser */
function MiniAppLayout({ label }) {
  return (
    <div style={{ display: 'flex', gap: '6px', height: '70px' }}>
      <div style={{ width: '40px', background: 'var(--surface2)', borderRadius: '4px', border: '1px solid var(--border)' }} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ height: '10px', background: 'var(--surface2)', borderRadius: '3px', border: '1px solid var(--border)', width: '60%' }} />
        <div style={{ flex: 1, background: 'var(--surface2)', borderRadius: '4px', border: '1px solid var(--border)' }} />
      </div>
    </div>
  );
}

/* ─── Wireframe: Server box ──────────────────────────────────────────────── */
function ServerBox({ label, layers, accentColor = GREEN }) {
  return (
    <div style={{
      border: `2px solid ${accentColor}66`, borderRadius: '10px', overflow: 'hidden',
      background: 'var(--surface)', boxShadow: `0 4px 24px ${accentColor}18`,
      minWidth: 0,
    }}>
      <div style={{
        background: accentColor + '18', padding: '8px 12px',
        borderBottom: `1px solid ${accentColor}33`,
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        <span style={{ fontSize: '0.9rem' }}>⚡</span>
        <span style={{ fontWeight: 700, fontSize: '0.78rem', color: accentColor }}>Lobees API Server</span>
      </div>
      <div style={{ padding: '10px' }}>
        {layers.map((layer, i) => (
          <div key={i} style={{
            padding: '5px 8px', marginBottom: i < layers.length - 1 ? '4px' : 0,
            background: 'var(--surface2)', borderRadius: '4px', border: '1px solid var(--border)',
            fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'JetBrains Mono, monospace',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            <span style={{ color: layer.color || accentColor }}>{layer.icon}</span>
            {layer.label}
          </div>
        ))}
      </div>
      <div style={{
        textAlign: 'center', padding: '5px', fontSize: '0.65rem',
        color: accentColor, fontWeight: 700, background: accentColor + '10',
        borderTop: `1px solid ${accentColor}33`,
      }}>{label}</div>
    </div>
  );
}

/* ─── Wireframe: n8n / External tool box ───────────────────────────────── */
function ToolBox({ toolName, toolColor, children }) {
  return (
    <div style={{
      border: `2px solid ${toolColor}66`, borderRadius: '10px', overflow: 'hidden',
      background: 'var(--surface)', boxShadow: `0 4px 24px ${toolColor}18`,
      minWidth: 0,
    }}>
      <div style={{
        background: toolColor + '18', padding: '8px 12px',
        borderBottom: `1px solid ${toolColor}33`,
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        <div style={{
          width: '22px', height: '22px', borderRadius: '5px',
          background: toolColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.65rem', fontWeight: 900, color: '#fff',
        }}>
          {toolName.slice(0, 2).toUpperCase()}
        </div>
        <span style={{ fontWeight: 700, fontSize: '0.78rem', color: toolColor }}>{toolName}</span>
      </div>
      <div style={{ padding: '10px' }}>{children}</div>
    </div>
  );
}

/* Mini row inside a tool box */
function ToolRow({ label, value, highlight }) {
  return (
    <div style={{ marginBottom: '4px', fontSize: '0.68rem', display: 'flex', gap: '6px', alignItems: 'baseline' }}>
      <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{label}:</span>
      <span style={{
        fontFamily: 'JetBrains Mono, monospace',
        color: highlight || 'var(--text)',
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );
}

/* ─── Labelled arrow ─────────────────────────────────────────────────────── */
function FlowArrow({ label, subLabel, color, dir = 'right', step }) {
  const isRight = dir === 'right';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '0 4px' }}>
      {step && (
        <span style={{
          fontSize: '0.62rem', fontWeight: 800, color,
          background: color + '22', border: `1px solid ${color}44`,
          borderRadius: '10px', padding: '1px 7px', letterSpacing: '0.04em',
        }}>STEP {step}</span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        {!isRight && <span style={{ color, fontSize: '1rem', flexShrink: 0 }}>◀</span>}
        <div style={{ flex: 1, height: '2px', background: `linear-gradient(${isRight ? '90deg' : '270deg'}, ${color}00, ${color})` }} />
        {isRight && <span style={{ color, fontSize: '1rem', flexShrink: 0 }}>▶</span>}
      </div>
      <div style={{
        background: color + '15', border: `1px solid ${color}44`, borderRadius: '5px',
        padding: '4px 8px', textAlign: 'center', width: '100%', boxSizing: 'border-box',
      }}>
        <div style={{ fontSize: '0.7rem', fontWeight: 700, color, fontFamily: 'JetBrains Mono, monospace' }}>{label}</div>
        {subLabel && <div style={{ fontSize: '0.62rem', color: 'var(--muted)', marginTop: '1px' }}>{subLabel}</div>}
      </div>
    </div>
  );
}

/* ─── Settings page wireframe (for "where to find token") ───────────────── */
function SettingsWireframe() {
  return (
    <div style={{
      border: `2px solid ${AMBER}66`, borderRadius: '10px', overflow: 'hidden',
      background: 'var(--surface)', boxShadow: `0 4px 20px ${AMBER}18`,
    }}>
      {/* Browser chrome */}
      <div style={{
        background: 'var(--surface2)', padding: '6px 10px',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: '6px',
      }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['#ff5f56', '#ffbd2e', '#27c93f'].map(c => (
            <div key={c} style={{ width: '8px', height: '8px', borderRadius: '50%', background: c }} />
          ))}
        </div>
        <div style={{
          flex: 1, background: 'var(--bg)', borderRadius: '3px',
          padding: '2px 8px', fontSize: '0.62rem', color: 'var(--muted)',
          fontFamily: 'JetBrains Mono, monospace', border: '1px solid var(--border)',
        }}>lobees.com/admin/profile/settings</div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: '0', borderBottom: '1px solid var(--border)',
        background: 'var(--surface)', overflowX: 'auto',
      }}>
        {['Mail Settings', 'Connect to Apps', 'API Token', 'Security'].map(tab => (
          <div key={tab} style={{
            padding: '7px 14px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
            color: tab === 'API Token' ? AMBER : 'var(--muted)',
            borderBottom: tab === 'API Token' ? `2px solid ${AMBER}` : '2px solid transparent',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>{tab}</div>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)', marginBottom: '3px' }}>API Access</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '14px' }}>
          Use these credentials to connect external tools like Postman, N8N, Zapier, or any custom integration.
        </div>

        {/* Base URL row */}
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text)', marginBottom: '5px' }}>API Base URL</div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          <div style={{
            flex: 1, padding: '6px 10px', background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: '5px',
            fontSize: '0.72rem', color: 'var(--muted)', fontFamily: 'JetBrains Mono, monospace',
          }}>https://api.lobees.com</div>
          <div style={{
            padding: '6px 12px', background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: '5px',
            fontSize: '0.7rem', color: 'var(--text)', cursor: 'pointer',
          }}>⎘ Copy</div>
        </div>

        {/* Token row */}
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text)', marginBottom: '5px' }}>
          Your Auth Token
        </div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
          <div style={{
            flex: 1, padding: '6px 10px', background: 'var(--surface2)',
            border: `1.5px solid ${AMBER}77`, borderRadius: '5px',
            fontSize: '0.62rem', color: AMBER, fontFamily: 'JetBrains Mono, monospace',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6IjY4...</div>
          <div style={{
            padding: '6px 12px', background: AMBER + '22',
            border: `1px solid ${AMBER}55`, borderRadius: '5px',
            fontSize: '0.7rem', color: AMBER, cursor: 'pointer', fontWeight: 700,
          }}>⎘ Copy</div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px',
          fontSize: '0.66rem', color: 'var(--muted)',
        }}>
          <span style={{ color: AMBER }}>ℹ</span>
          Pass this token in the <span style={{ color: AMBER, fontFamily: 'JetBrains Mono, monospace', margin: '0 2px' }}>Authorization</span> header as{' '}
          <span style={{ color: AMBER, fontFamily: 'JetBrains Mono, monospace', margin: '0 2px' }}>Bearer &lt;token&gt;</span> with every API request.
        </div>
      </div>

      <div style={{
        textAlign: 'center', padding: '5px', fontSize: '0.65rem',
        color: AMBER, fontWeight: 700, background: AMBER + '10',
        borderTop: `1px solid ${AMBER}33`,
      }}>Profile → Settings → API Token tab</div>
    </div>
  );
}

/* ─── React Auth Flow Diagram (pictorial) ────────────────────────────────── */
function ReactAuthDiagram() {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {/* Phase 1: Login */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: '10px' }}>
        PHASE 1 — LOGIN
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        <BrowserFrame title="React App (Browser)" url="lobees.com/admin/login" accentColor={PURPLE}>
          <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: '6px' }}>Login Form</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {['Email', 'Password'].map(f => (
              <div key={f} style={{ height: '18px', background: 'var(--surface2)', borderRadius: '3px', border: '1px solid var(--border)', padding: '0 6px', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '0.62rem', color: 'var(--muted)' }}>{f}</span>
              </div>
            ))}
            <div style={{ height: '18px', background: PURPLE + '22', border: `1px solid ${PURPLE}55`, borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: PURPLE }}>Sign In</span>
            </div>
          </div>
        </BrowserFrame>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '160px' }}>
          <FlowArrow label="POST /auth/login" subLabel='{ email, password }' color={PURPLE} dir="right" step="1" />
          <FlowArrow label="Set-Cookie: token=jwt" subLabel="HttpOnly · Secure · SameSite" color={GREEN} dir="left" />
        </div>

        <ServerBox label="Express + isauth middleware" layers={[
          { icon: '🔐', label: 'bcrypt.compare()', color: PURPLE },
          { icon: '✍', label: 'jwt.sign({ userid })', color: PURPLE },
          { icon: '🍪', label: 'res.cookie("token", jwt)', color: GREEN_RAW },
        ]} accentColor={GREEN} />
      </div>

      {/* Phase 2: Authenticated Request */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: '10px' }}>
        PHASE 2 — EVERY SUBSEQUENT REQUEST
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center' }}>
        <BrowserFrame title="React App (any page)" url="lobees.com/admin/leads" accentColor={PURPLE}>
          <MiniAppLayout />
          <div style={{ marginTop: '6px', padding: '4px 8px', background: PURPLE + '15', borderRadius: '4px', border: `1px solid ${PURPLE}33` }}>
            <div style={{ fontSize: '0.62rem', color: PURPLE, fontFamily: 'JetBrains Mono, monospace' }}>getData(url)</div>
            <div style={{ fontSize: '0.6rem', color: 'var(--muted)' }}>Cookie sent automatically by browser</div>
          </div>
        </BrowserFrame>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '160px' }}>
          <FlowArrow label="GET /api/lead/getallleads" subLabel="Cookie: token=jwt  (auto)" color={PURPLE} dir="right" step="2" />
          <FlowArrow label="200 OK + data" subLabel="isauth verified cookie" color={GREEN} dir="left" />
        </div>

        <ServerBox label="Express + isauth middleware" layers={[
          { icon: '🍪', label: 'req.cookies.token', color: AMBER },
          { icon: '✅', label: 'jwt.verify(token)', color: GREEN_RAW },
          { icon: '📦', label: 'Controller → MongoDB', color: 'var(--muted)' },
        ]} accentColor={GREEN} />
      </div>
    </div>
  );
}

/* ─── External Auth Flow Diagram (pictorial) ─────────────────────────────── */
function ExternalAuthDiagram() {
  return (
    <div style={{ marginBottom: '1rem' }}>
      {/* Step 1: Get token from settings */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: '10px' }}>
        STEP 1 — GET YOUR TOKEN FROM THE APP
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center', marginBottom: '20px' }}>
        <BrowserFrame title="Logged-in user" url="lobees.com/admin/profile/settings" accentColor={AMBER}>
          <div style={{ fontSize: '0.68rem', color: 'var(--muted)', marginBottom: '5px' }}>Profile → Settings → API Token</div>
          <div style={{ padding: '6px 8px', background: AMBER + '15', border: `1px solid ${AMBER}44`, borderRadius: '5px' }}>
            <div style={{ fontSize: '0.62rem', color: 'var(--muted)', marginBottom: '3px' }}>Your Auth Token</div>
            <div style={{ fontSize: '0.6rem', color: AMBER, fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>eyJhbGci...kpXVCJ9</div>
          </div>
          <div style={{ marginTop: '5px', display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ padding: '3px 10px', background: AMBER + '22', border: `1px solid ${AMBER}55`, borderRadius: '4px', fontSize: '0.62rem', color: AMBER, fontWeight: 700 }}>⎘ Copy Token</div>
          </div>
        </BrowserFrame>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '140px', alignItems: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--muted)', textAlign: 'center' }}>copy & paste into</div>
          <div style={{ fontSize: '1.4rem' }}>→</div>
        </div>

        <ToolBox toolName="n8n / Postman" toolColor={AMBER}>
          <ToolRow label="Credential type" value="Header Auth" />
          <ToolRow label="Name" value="Authorization" highlight={AMBER} />
          <ToolRow label="Value" value="Bearer eyJhbGci..." highlight={AMBER} />
          <div style={{
            marginTop: '6px', padding: '4px 8px', background: AMBER + '12',
            borderRadius: '4px', border: `1px dashed ${AMBER}44`,
            fontSize: '0.6rem', color: 'var(--muted)', textAlign: 'center',
          }}>Store once as a credential · reuse everywhere</div>
        </ToolBox>
      </div>

      {/* Step 2: Make API call */}
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: '10px' }}>
        STEP 2 — MAKE AUTHENTICATED API CALLS
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'center' }}>
        <ToolBox toolName="n8n Workflow" toolColor={AMBER}>
          <div style={{ fontSize: '0.68rem', color: 'var(--muted)', marginBottom: '5px' }}>HTTP Request Node</div>
          <ToolRow label="Method" value="GET" />
          <ToolRow label="URL" value="/api/lead/getallleads" highlight="var(--accent)" />
          <ToolRow label="Authorization" value="Bearer eyJhbGci..." highlight={AMBER} />
          <div style={{
            marginTop: '6px', height: '16px', background: 'var(--surface2)',
            borderRadius: '3px', border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', paddingLeft: '6px',
          }}>
            <span style={{ fontSize: '0.6rem', color: 'var(--muted)' }}>▶ Execute Node</span>
          </div>
        </ToolBox>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '160px' }}>
          <FlowArrow label="GET /api/lead/getallleads" subLabel="Authorization: Bearer <jwt>" color={AMBER} dir="right" step="2" />
          <FlowArrow label="200 OK + data" subLabel="isauthapi verified Bearer token" color={GREEN} dir="left" />
        </div>

        <ServerBox label="Express + isauthapi middleware" layers={[
          { icon: '🔑', label: 'req.headers.authorization', color: AMBER },
          { icon: '✅', label: 'jwt.verify(bearerToken)', color: GREEN_RAW },
          { icon: '📦', label: 'Controller → MongoDB', color: 'var(--muted)' },
        ]} accentColor={GREEN} />
      </div>
    </div>
  );
}

/* ─── Page sections ──────────────────────────────────────────────────────── */
function OverviewSection() {
  const colors = { GET: GREEN, POST: PURPLE, PUT: AMBER, DELETE: '#ff6584' };
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg, ${PURPLE}18 0%, ${GREEN}12 100%)`,
        border: '1px solid var(--border)', borderRadius: '12px',
        padding: '24px', marginBottom: '2rem',
      }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text)', marginBottom: '8px' }}>Authentication</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.92rem', lineHeight: 1.75, maxWidth: '680px', margin: 0 }}>
          Lobees API uses <strong style={{ color: PURPLE }}>JWT (JSON Web Tokens)</strong> for all authentication.
          Choose the mechanism that fits your integration.
        </p>
      </div>

      <Section title="Two Auth Mechanisms">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '1.5rem' }}>
          <div style={{ background: 'var(--surface2)', border: `1.5px solid ${PURPLE}44`, borderRadius: '10px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '1.4rem' }}>🍪</span>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.95rem' }}>HttpOnly Cookie</div>
                <div style={{ fontSize: '0.72rem', color: PURPLE }}>React / Browser apps</div>
              </div>
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.9 }}>
              <li>Set automatically on login</li>
              <li>Browser sends it on every request automatically</li>
              <li>Not readable by JavaScript — XSS-safe</li>
              <li>Middleware: <Inline color={PURPLE}>isauth.js</Inline></li>
            </ul>
          </div>
          <div style={{ background: 'var(--surface2)', border: `1.5px solid ${GREEN}44`, borderRadius: '10px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <span style={{ fontSize: '1.4rem' }}>🔑</span>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.95rem' }}>Bearer Token</div>
                <div style={{ fontSize: '0.72rem', color: GREEN }}>n8n · Postman · Retell · APIs</div>
              </div>
            </div>
            <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.9 }}>
              <li>Set manually in the <Inline color={GREEN}>Authorization</Inline> header</li>
              <li><Inline color={GREEN}>Authorization: Bearer &lt;token&gt;</Inline></li>
              <li>Works from any HTTP client, automation tool</li>
              <li>Middleware: <Inline color={GREEN}>isauthapi.js</Inline></li>
            </ul>
          </div>
        </div>
        <InfoBox type="warning">
          The old <Inline>?token=&lt;jwt&gt;</Inline> query parameter is <strong>no longer supported</strong>.
          All authenticated endpoints now require the token in a cookie or <Inline>Authorization</Inline> header.
        </InfoBox>
      </Section>

      <Section title="Auth Endpoints at a Glance">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['Method', 'Endpoint', 'Auth', 'Purpose'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: 'var(--muted)', fontWeight: 600, fontSize: '0.72rem' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ['POST', '/api/auth/login',        '—',             'Email + password login; sets HttpOnly cookie'],
              ['POST', '/api/auth/register',      '—',             'Register new account; sends OTP to email'],
              ['POST', '/api/auth/getotp',        '—',             'Request a one-time password'],
              ['POST', '/api/auth/validateotp',   '—',             'Verify OTP; creates session on success'],
              ['PUT',  '/api/auth/updatepassword','—',             'Reset password by email'],
              ['GET',  '/api/auth/validateuser',  'Cookie/Bearer', 'Restore session on page load'],
              ['POST', '/api/auth/logout',        'Cookie/Bearer', 'Clear session cookie for this device'],
              ['POST', '/api/auth/logout-all',    'Cookie/Bearer', 'Invalidate all sessions (bump tokenVersion)'],
              ['GET',  '/api/auth/sessions',      'Cookie/Bearer', 'List all active login sessions'],
              ['GET',  '/api/auth/token',         'Cookie/Bearer', 'Get JWT for use in external tools'],
            ].map(([method, path, auth, desc], i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '7px 10px' }}>
                  <span style={{ background: (colors[method] || '#888') + '20', color: colors[method] || '#888', border: `1px solid ${(colors[method] || '#888')}55`, borderRadius: '4px', padding: '2px 7px', fontSize: '0.67rem', fontWeight: 700 }}>{method}</span>
                </td>
                <td style={{ padding: '7px 10px', fontFamily: 'JetBrains Mono, monospace', color: 'var(--text)', fontSize: '0.76rem' }}>{path}</td>
                <td style={{ padding: '7px 10px', color: auth === '—' ? 'var(--muted)' : GREEN, fontSize: '0.76rem', whiteSpace: 'nowrap' }}>{auth}</td>
                <td style={{ padding: '7px 10px', color: 'var(--muted)', fontSize: '0.8rem' }}>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </>
  );
}

function InternalSection() {
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg, ${PURPLE}18 0%, transparent 100%)`,
        border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '2rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '1.5rem' }}>⚛️</span>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>React / Internal Apps</h1>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.75, margin: 0 }}>
          For browser-based apps (Lobees Admin, Lobees Frontend), authentication uses an{' '}
          <strong style={{ color: PURPLE }}>HttpOnly cookie</strong>. The browser manages the token entirely —
          you never touch it from JavaScript.
        </p>
      </div>

      <Section title="Auth Flow">
        <ReactAuthDiagram />
        <InfoBox type="tip">
          Because the cookie is <strong>HttpOnly</strong>, JavaScript cannot read it — this protects against XSS attacks.
          The browser attaches it automatically on every same-origin request with <Inline color={PURPLE}>credentials: 'include'</Inline>.
        </InfoBox>
      </Section>

      <Section title="Step 1 — Login">
        <SubSection title="POST /api/auth/login">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
            On success the API sets a <Inline color={PURPLE}>token</Inline> HttpOnly cookie and returns the full user session.
            Store the returned <Inline color={PURPLE}>data</Inline> in your app state.
          </p>
          <CodeBlock lang="javascript" code={`// fetch — credentials:'include' makes the browser accept & store the cookie
const res = await fetch('https://api.lobees.com/api/auth/login', {
  method: 'POST',
  credentials: 'include',          // ← required
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ email, password }),
});
const { status, data } = await res.json();
if (status) dispatch(setUser(data));`} />
          <CodeBlock lang="javascript" code={`// Axios — set withCredentials once on the shared instance
const api = axios.create({
  baseURL: 'https://api.lobees.com/api',
  withCredentials: true,           // ← required
});
const { data } = await api.post('/auth/login', { email, password });`} />
        </SubSection>
      </Section>

      <Section title="Step 2 — Restore Session on Page Load">
        <SubSection title="GET /api/auth/validateuser">
          <CodeBlock lang="javascript" code={`useEffect(() => {
  const restoreSession = async () => {
    const res = await fetch('https://api.lobees.com/api/auth/validateuser', {
      credentials: 'include',      // ← sends the stored cookie
    });
    const { status, data } = await res.json();
    status ? dispatch(setUser(data)) : dispatch(clearUser());
  };
  restoreSession();
}, []);`} />
        </SubSection>
      </Section>

      <Section title="Step 3 — Making Authenticated Requests">
        <CodeBlock lang="javascript" code={`// fetch — credentials:'include' sends the cookie automatically
const res = await fetch('https://api.lobees.com/api/lead/getallleads', {
  credentials: 'include',
});
const { data } = await res.json();`} />
        <CodeBlock lang="javascript" code={`// Axios with withCredentials:true on instance — cookie sent automatically
const { data } = await api.get('/lead/getallleads');`} />
        <InfoBox type="info">
          Make sure your API server's CORS config includes <Inline>credentials: true</Inline> and your
          frontend origin in <Inline>allowedOrigins</Inline>. Without this, the browser will block cookies.
        </InfoBox>
      </Section>

      <Section title="Session Management">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '1.25rem' }}>
          {[
            { icon: '🚪', title: 'Logout', ep: 'POST /auth/logout', desc: 'Clears the HttpOnly cookie on this device.' },
            { icon: '🌐', title: 'Logout All Devices', ep: 'POST /auth/logout-all', desc: 'Bumps tokenVersion — all JWTs become invalid immediately.' },
            { icon: '📋', title: 'Active Sessions', ep: 'GET /auth/sessions', desc: 'Returns all active login sessions with device & time.' },
          ].map(({ icon, title, ep, desc }) => (
            <div key={title} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px' }}>
              <div style={{ fontSize: '1.2rem', marginBottom: '6px' }}>{icon}</div>
              <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--text)', marginBottom: '4px' }}>{title}</div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', color: PURPLE, marginBottom: '6px' }}>{ep}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.6 }}>{desc}</div>
            </div>
          ))}
        </div>
        <CodeBlock lang="javascript" code={`await fetch('https://api.lobees.com/api/auth/logout',     { method: 'POST', credentials: 'include' });
await fetch('https://api.lobees.com/api/auth/logout-all', { method: 'POST', credentials: 'include' });`} />
      </Section>

      <Section title="OTP / Passwordless Flows">
        <SubSection title="New user — no password">
          <CodeBlock lang="text" code={`POST /api/auth/getotp      { email }          → OTP sent to email
POST /api/auth/validateotp { email, otp }     → Account created, cookie set`} />
        </SubSection>
        <SubSection title="Existing user — forgot password">
          <CodeBlock lang="text" code={`POST /api/auth/getotp        { email }             → OTP sent to email
PUT  /api/auth/updatepassword { email, password } → Password updated
POST /api/auth/login          { email, password } → Cookie set`} />
        </SubSection>
      </Section>

      <Section title="Local Development Setup">
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
          When running React locally against the remote API, the browser blocks the cookie because
          the origins differ (<Inline color={PURPLE}>localhost</Inline> vs <Inline color={PURPLE}>api.lobees.com</Inline>).
          The CRA dev proxy solves this transparently — all API calls are forwarded through the local dev server,
          making them appear same-origin to the browser.
        </p>
        <SubSection title="1. Set the proxy in package.json (per branch)">
          <CodeBlock lang="json" code={`// package.json — dev branch
{
  "name": "your-app",
  "proxy": "https://api.lobees.com",
  ...
}`} />
          <InfoBox type="info">
            Change the proxy URL to match the branch's API server. This only affects <Inline color={PURPLE}>npm start</Inline> — it has no effect on the production build.
          </InfoBox>
        </SubSection>
        <SubSection title="2. Use relative API URL in .env">
          <CodeBlock lang="text" code={`# .env (committed)
REACT_APP_API_URL=/api
REACT_APP_SERVER_URL=/`} />
        </SubSection>
        <SubSection title="3. Switching to local API">
          <CodeBlock lang="text" code={`# .env — when testing against local Node server
REACT_APP_API_URL=http://localhost:4006/api
REACT_APP_SERVER_URL=http://localhost:4006/

# Revert before committing!`} />
          <InfoBox type="warning">
            Remember to revert <Inline color={AMBER}>REACT_APP_API_URL</Inline> back to <Inline color={AMBER}>/api</Inline> before committing. The proxy is bypassed automatically when an absolute URL is set.
          </InfoBox>
        </SubSection>
      </Section>
    </>
  );
}

function ExternalSection() {
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg, ${AMBER}18 0%, transparent 100%)`,
        border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '2rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '1.5rem' }}>🔗</span>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>External API Access</h1>
        </div>
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
          {[['n8n', AMBER], ['Postman', '#ff6b35'], ['Retell AI', '#43c6ff'], ['Zapier', '#ff4a00'], ['Any HTTP client', GREEN]].map(([l, c]) => (
            <span key={l} style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, background: c + '20', color: c, border: `1px solid ${c}44` }}>{l}</span>
          ))}
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.75, margin: 0 }}>
          External tools cannot use cookies. Send your JWT in the{' '}
          <strong style={{ color: AMBER }}>Authorization: Bearer</strong> header on every request.
        </p>
      </div>

      <Section title="Auth Flow">
        <ExternalAuthDiagram />
      </Section>

      <Section title="Step 1 — Get Your Token">
        <SubSection title="Option A — Via Login API (programmatic / server-side)">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
            For automated workflows (n8n, server scripts, CI pipelines), call the login endpoint once
            and extract the <Inline color={AMBER}>token</Inline> from the response body. Store it in an environment variable — never hardcode it.
          </p>
          <CodeBlock lang="http" code={`POST https://api.lobees.com/api/auth/login
Content-Type: application/json

{
  "email": "you@example.com",
  "password": "yourpassword"
}`} />
          <CodeBlock lang="json" code={`// Response
{
  "status": true,
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "data": { /* user object */ },
  ...
}`} />
          <InfoBox type="tip">
            Copy the <Inline color={AMBER}>token</Inline> value and store it as an environment variable (e.g. <Inline color={AMBER}>LOBEES_TOKEN</Inline>).
            Use it as <Inline color={AMBER}>Authorization: Bearer {'<token>'}</Inline> on all subsequent requests.
          </InfoBox>
        </SubSection>

        <SubSection title="Option B — Copy from the App (manual)">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
            For one-off testing in Postman or to configure n8n manually — log in to the Lobees app
            and copy your token from profile settings:
          </p>

        {/* Steps visual */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.25rem', flexWrap: 'wrap',
        }}>
          {[
            { n: '1', label: 'Log in to Lobees', icon: '🔐' },
            { n: '2', label: 'Go to Profile', icon: '👤' },
            { n: '3', label: 'Settings tab', icon: '⚙️' },
            { n: '4', label: 'API Token tab', icon: '🔑' },
            { n: '5', label: 'Copy token', icon: '⎘' },
          ].map(({ n, label, icon }, i, arr) => (
            <React.Fragment key={n}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: AMBER + '22', border: `2px solid ${AMBER}66`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
                }}>{icon}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--muted)', textAlign: 'center', maxWidth: '60px' }}>{label}</div>
              </div>
              {i < arr.length - 1 && (
                <div style={{ color: AMBER, fontSize: '1rem', marginBottom: '16px' }}>→</div>
              )}
            </React.Fragment>
          ))}
        </div>

        <SettingsWireframe />

        <div style={{ marginTop: '1rem' }}>
          <InfoBox type="tip">
            Once you have the token, paste it into n8n's <strong>Header Auth credential</strong> (Name:{' '}
            <Inline color={AMBER}>Authorization</Inline>, Value: <Inline color={AMBER}>Bearer &lt;token&gt;</Inline>) or
            Postman's <strong>Authorization tab → Bearer Token</strong>. Store it once and reuse it across all requests.
          </InfoBox>
        </div>
        </SubSection>
      </Section>

      <Section title="Step 2 — Use the Token in Every Request">
        <SubSection title="HTTP header format">
          <CodeBlock lang="http" code={`GET https://api.lobees.com/api/lead/getallleads
Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`} />
          <CodeBlock lang="http" code={`POST https://api.lobees.com/api/lead/createlead
Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
Content-Type: application/json

{
  "email": "lead@example.com",
  "name": "Jane"
}`} />
        </SubSection>
      </Section>

      <Section title="Platform-Specific Setup">
        <SubSection title="n8n — HTTP Request Node">
          <CodeBlock lang="text" code={`Node type : HTTP Request
URL        : https://api.lobees.com/api/lead/getallleads
Method     : GET
Auth type  : Generic Credential → Header Auth
  Name     : Authorization
  Value    : Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...`} />
          <InfoBox type="tip">
            Save it as an n8n credential named <strong>"Lobees API"</strong> so every workflow node can
            reference it — you only need to update it in one place when the token rotates.
          </InfoBox>
        </SubSection>

        <SubSection title="Postman">
          <CodeBlock lang="text" code={`1. Open collection → Edit → Authorization
2. Type: Bearer Token
3. Token: eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...

── or use a collection variable ─────────────────────
1. Collection variables: lobeesToken = <your-jwt>
2. Authorization → Bearer Token → {{lobeesToken}}`} />
        </SubSection>

        <SubSection title="Retell AI — Webhook / Custom LLM">
          <CodeBlock lang="json" code={`{
  "headers": {
    "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
    "Content-Type": "application/json"
  }
}`} />
        </SubSection>

        <SubSection title="JavaScript / Node.js">
          <CodeBlock lang="javascript" code={`const TOKEN = process.env.LOBEES_TOKEN; // never hardcode

const res = await fetch('https://api.lobees.com/api/lead/getallleads', {
  headers: { 'Authorization': \`Bearer \${TOKEN}\` },
});
const { status, data } = await res.json();`} />
        </SubSection>

        <SubSection title="Python">
          <CodeBlock lang="python" code={`import os, requests

headers = { "Authorization": f"Bearer {os.environ['LOBEES_TOKEN']}" }
resp = requests.get("https://api.lobees.com/api/lead/getallleads", headers=headers)
data = resp.json()`} />
        </SubSection>
      </Section>

      <Section title="Token Security & Lifecycle">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { icon: '⏱', title: 'Token Expiry', desc: 'JWTs expire per the server JWT_SECRET config. Re-login or call GET /api/auth/token to refresh.' },
            { icon: '🔒', title: 'Logout All Devices', desc: 'POST /auth/logout-all bumps tokenVersion in DB — all previously issued tokens are instantly invalid.' },
            { icon: '🔐', title: 'Never commit tokens', desc: 'Store in environment variables or a secret manager. Never paste a real token into source control.' },
            { icon: '🔄', title: 'Rotate regularly', desc: 'For long-running n8n workflows, refresh by visiting Profile → Settings → API Token and copying a fresh JWT.' },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px', display: 'flex', gap: '10px' }}>
              <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{icon}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.84rem', color: 'var(--text)', marginBottom: '4px' }}>{title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.6 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Test API — Try it from the docs">
        <SubSection title="Step 1 — Configure API (ApiConfigModal)">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
            Open the API Configuration modal (click the user / settings icon in the docs toolbar), then paste
            your API <Inline color={AMBER}>Base URL</Inline> and the <Inline color={AMBER}>Bearer token</Inline> into the
            fields. Click <strong>Save</strong> — the values are stored locally in your browser and used by the
            in-page request tester.
          </p>
          <InfoBox type="tip">
            This toolbar only appears on the actual API reference pages in the left sidebar — for example{' '}
            <strong>CRM Module → Leads</strong>, <strong>Auth API Reference → Auth Endpoints</strong>, <strong>Company</strong>,{' '}
            <strong>Project</strong>, etc. (It is not shown on this Authentication guide itself.) Open any of those pages,
            look to the top-right toolbar and click the user icon to open the API Configuration modal. Paste your Base URL
            and Bearer token there and click <strong>Save</strong>.

            Below is a quick preview of the modal so you can recognize it in the UI:
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 520, background: '#0d0d16', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, color: 'var(--text)' }}>API Configuration</div>
                  <div style={{ color: 'var(--muted)' }}>✕</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>BASE URL</div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: 10, borderRadius: 6, fontFamily: 'JetBrains Mono, monospace', marginBottom: 10 }}>https://api.lobees.com</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>BEARER TOKEN</div>
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: 10, borderRadius: 6, color: 'var(--muted)', marginBottom: 12 }}>Paste your JWT here</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <div style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', color: 'var(--muted)' }}>Cancel</div>
                  <div style={{ padding: '8px 12px', borderRadius: 8, background: 'linear-gradient(90deg,#ff7ab6,#7a5cff)', color: '#fff' }}>Save</div>
                </div>
              </div>
            </div>
          </InfoBox>
        </SubSection>

        <SubSection title="Step 2 — Use Test Now (EndpointCard)">
          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
            With credentials saved, open any endpoint in the API reference and expand the "Test Now" panel.
            Fill path parameters, query parameters, and request body as needed, then click <strong>Send Request</strong>.
            The saved token is automatically applied to the <Inline color={AMBER}>Authorization</Inline> header.
          </p>

          <CodeBlock lang="http" code={`GET https://api.your-host.com/api/lead/getallleads
Authorization: Bearer <your-jwt>`} />

          <InfoBox type="tip">
            The test UI is implemented in [app-docs/src/components/EndpointCard.js](src/components/EndpointCard.js#L1).
            Use the origin override input if you need to point a single request at a different host without changing the saved Base URL.
          </InfoBox>
        </SubSection>
      </Section>
    </>
  );
}

/* ─── Real Settings screen wireframe (pixel-accurate to the app UI) ─────── */
const LOBEES_RED = '#eb5245';

function RealSettingsWireframe() {
  const profileTabs = ['Profile Info', 'Privacy', 'Password', 'Availability', 'Settings', 'Survey Invites', 'Timeline'];
  const settingsTabs = ['Mail Settings', 'Connect to Apps', 'API Token', 'Security'];
  return (
    <div style={{
      background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '10px',
      overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      fontFamily: 'system-ui, -apple-system, sans-serif', marginBottom: '1rem',
    }}>
      {/* Profile tabs row */}
      <div style={{ display: 'flex', gap: '26px', padding: '14px 20px 0', borderBottom: '1px solid #eef0f2', overflowX: 'auto' }}>
        {profileTabs.map(tab => (
          <div key={tab} style={{
            paddingBottom: '12px', fontSize: '0.76rem', fontWeight: 600, whiteSpace: 'nowrap',
            color: tab === 'Settings' ? LOBEES_RED : '#374151',
            borderBottom: tab === 'Settings' ? `2px solid ${LOBEES_RED}` : '2px solid transparent',
          }}>{tab}</div>
        ))}
      </div>

      {/* Breadcrumb */}
      <div style={{ padding: '10px 20px', fontSize: '0.72rem', color: '#6b7280', borderBottom: '1px solid #eef0f2' }}>
        Tablero <span style={{ margin: '0 4px' }}>/</span> Usuario de la Aplicación <span style={{ margin: '0 4px' }}>/</span> Settings <span style={{ margin: '0 4px' }}>/</span> Admin MpForAll
      </div>

      {/* Settings sub-tabs */}
      <div style={{ display: 'flex', gap: '8px', padding: '12px 20px', borderBottom: '1px solid #eef0f2' }}>
        {settingsTabs.map(tab => (
          <div key={tab} style={{
            padding: '7px 14px', fontSize: '0.74rem', fontWeight: 600, borderRadius: '6px',
            color: tab === 'API Token' ? LOBEES_RED : '#6b7280',
            border: tab === 'API Token' ? `1.5px solid ${LOBEES_RED}` : '1px solid transparent',
          }}>{tab}</div>
        ))}
      </div>

      {/* API Access card */}
      <div style={{ padding: '18px 20px 20px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#111827', marginBottom: '4px' }}>API Access</div>
        <div style={{ fontSize: '0.76rem', color: '#6b7280', marginBottom: '16px' }}>
          Use these credentials to connect external tools like Postman, N8N, Zapier, or any custom integration.
        </div>

        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>API Base URL</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <div style={{
            flex: 1, padding: '9px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb',
            borderRadius: '6px', fontSize: '0.78rem', color: '#374151', fontFamily: 'JetBrains Mono, monospace',
          }}>https://api.lobees.com</div>
          <div style={{
            padding: '9px 16px', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: '6px',
            fontSize: '0.74rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600,
          }}>⧉ Copy</div>
        </div>

        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#111827', marginBottom: '6px' }}>Your Auth Token</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <div style={{
            flex: 1, padding: '9px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb',
            borderRadius: '6px', fontSize: '0.66rem', color: '#374151', fontFamily: 'JetBrains Mono, monospace',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6...</div>
          <div style={{
            padding: '9px 16px', background: '#ffffff', border: '1px solid #d1d5db', borderRadius: '6px',
            fontSize: '0.74rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 600,
          }}>⧉ Copy</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.7rem', color: '#6b7280' }}>
          <span>ⓘ</span>
          <span>
            Pass this token in the <strong style={{ color: LOBEES_RED }}>Authorization</strong> header as{' '}
            <strong style={{ color: LOBEES_RED }}>Bearer &lt;token&gt;</strong> with every API request.
          </span>
        </div>
      </div>

      <div style={{
        textAlign: 'center', padding: '6px', fontSize: '0.64rem', fontWeight: 700,
        color: LOBEES_RED, background: LOBEES_RED + '0d', borderTop: `1px solid ${LOBEES_RED}33`,
      }}>Profile → Settings → API Token tab</div>
    </div>
  );
}

/* ─── Docs topbar preview (points at the real config button) ────────────── */
function TopbarPreview() {
  return (
    <div style={{
      display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px',
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
      padding: '10px 16px', marginBottom: '1rem',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
        background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '20px',
        fontSize: '0.66rem', color: 'var(--muted)', fontFamily: 'JetBrains Mono, monospace',
      }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: GREEN_RAW, display: 'inline-block' }} />
        api.lobees.com
      </div>
      <div style={{ position: 'relative' }}>
        <div style={{
          position: 'absolute', bottom: '38px', left: '50%', transform: 'translateX(-50%)',
          fontSize: '0.62rem', fontWeight: 800, color: AMBER, whiteSpace: 'nowrap',
          background: AMBER + '18', border: `1px solid ${AMBER}55`, borderRadius: '10px', padding: '2px 8px',
        }}>click here ↓</div>
        <div style={{
          width: '30px', height: '30px', borderRadius: '8px',
          border: `2px solid ${AMBER}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: AMBER, background: AMBER + '18',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
          </svg>
        </div>
      </div>
      <div style={{
        width: '30px', height: '30px', borderRadius: '8px', border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem',
      }}>🌙</div>
    </div>
  );
}

/* ─── API Configuration modal preview (mirrors ApiConfigModal.js) ───────── */
function ApiConfigModalPreview() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: 520, maxWidth: '100%', background: '#0d0d16', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontWeight: 700, color: '#fff' }}>API Configuration</div>
          <div style={{ color: '#9ca3af' }}>✕</div>
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>BASE URL</div>
        <div style={{ background: 'rgba(255,255,255,0.04)', padding: 10, borderRadius: 6, color: '#e2e4f0', fontFamily: 'JetBrains Mono, monospace', marginBottom: 10, fontSize: '0.78rem' }}>https://api.lobees.com</div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 6 }}>BEARER TOKEN</div>
        <div style={{ background: 'rgba(255,255,255,0.04)', padding: 10, borderRadius: 6, color: '#9ca3af', marginBottom: 12, fontSize: '0.78rem' }}>••••••••••••••••••••••••</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <div style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', color: '#9ca3af', fontSize: '0.78rem' }}>Cancel</div>
          <div style={{ padding: '8px 12px', borderRadius: 8, background: `linear-gradient(90deg, ${AMBER}, #ff7a45)`, color: '#000', fontWeight: 700, fontSize: '0.78rem' }}>Save</div>
        </div>
      </div>
    </div>
  );
}

function TestSection() {
  const [configOpen, setConfigOpen] = useState(false);
  return (
    <>
      <div style={{
        background: `linear-gradient(135deg, ${AMBER}18 0%, transparent 100%)`,
        border: '1px solid var(--border)', borderRadius: '12px', padding: '24px', marginBottom: '2rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '1.5rem' }}>🧪</span>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>Test API</h1>
        </div>
        <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.75, margin: 0 }}>
          Three steps to run any endpoint from these docs against your own account, in real time —
          no Postman or code required.
        </p>
      </div>

      {/* Step tracker */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {[
          { n: '1', label: 'Get your Auth Credentials' },
          { n: '2', label: 'Save them in this session' },
          { n: '3', label: 'Test any API' },
        ].map(({ n, label }, i, arr) => (
          <React.Fragment key={n}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px',
              background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '20px',
            }}>
              <span style={{
                width: '20px', height: '20px', borderRadius: '50%', background: AMBER + '22',
                border: `1.5px solid ${AMBER}`, color: AMBER, fontSize: '0.68rem', fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{n}</span>
              <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
            {i < arr.length - 1 && <span style={{ color: 'var(--muted)', alignSelf: 'center' }}>→</span>}
          </React.Fragment>
        ))}
      </div>

      <Section title="Step 1 — Get Your Auth Credentials">
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
          Every request needs two things: an <Inline color={AMBER}>API Base URL</Inline> (which server your account
          lives on) and an <Inline color={AMBER}>Auth Token</Inline> (proves who you are). Log in to the Lobees app,
          then go to <strong>Profile → Settings → API Token</strong> — both values are shown together, ready to copy:
        </p>

        <RealSettingsWireframe />

        <InfoBox type="tip">
          Copy the <strong>API Base URL</strong> and <strong>Auth Token</strong> — you'll paste both into the docs in Step 2.
          The Base URL differs per project (e.g. <Inline color={AMBER}>api.stoneweg.lobees.com</Inline>), so always take it from
          your own account rather than assuming <Inline color={AMBER}>api.lobees.com</Inline>.
        </InfoBox>
      </Section>

      <Section title="Step 2 — Save Credentials in This Session">
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
          Every API reference page in the left sidebar (for example <strong>CRM Module → Leads</strong>,{' '}
          <strong>Auth API Reference → Auth Endpoints</strong>, <strong>Company</strong>, <strong>Project</strong>, and so on)
          has the same top-right toolbar. Click the user icon there to open the <strong>API Configuration</strong> modal —
          paste your Base URL and Bearer token and click <strong>Save</strong>. Both values are stored locally in your
          browser (not sent anywhere else) and reused automatically by every "Test Now" panel.
        </p>

        <InfoBox type="warning">
          This <strong>Authentication</strong> section (the page you're reading right now) and the{' '}
          <strong>Developer Guide</strong> pages are guides only — they don't show this toolbar since they have no live
          endpoints to test. Use the button below to open the same modal right here instead of navigating away.
        </InfoBox>

        <TopbarPreview />
        <ApiConfigModalPreview />

        <div style={{ display: 'flex', justifyContent: 'center', margin: '1.25rem 0' }}>
          <button
            onClick={() => setConfigOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
              background: AMBER + '18', border: `1.5px solid ${AMBER}`, borderRadius: '8px',
              color: AMBER, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            Open API Configuration
          </button>
        </div>

        <InfoBox type="info">
          This is the real modal used throughout the docs — try it now. It only closes via its{' '}
          <strong>Cancel</strong> button or the <strong>✕</strong> icon, so an accidental click outside never
          discards what you typed.
        </InfoBox>

        {configOpen && <ApiConfigModal onClose={() => setConfigOpen(false)} />}
      </Section>

      <Section title="Step 3 — Test Any API">
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
          Open any endpoint in the API reference and expand its <strong>"Test Now"</strong> panel. Fill in the path
          params, query params, or request body exactly as documented for that endpoint, then click{' '}
          <strong>Send Request</strong> — your saved token is applied automatically and the real response is shown
          right below the form.
        </p>
        <CodeBlock lang="http" code={`GET https://api.lobees.com/api/lead/getallleads
Authorization: Bearer <your-saved-token>   // ← added automatically`} />
      </Section>
    </>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */
export default function AuthGuidePage() {
  const { submoduleId } = useParams();
  const map = {
    overview: <OverviewSection />,
    internal: <InternalSection />,
    external: <ExternalSection />,
    test: <TestSection />,
  };
  return (
    <div className="module-page">
      <div className="module-content" style={{ maxWidth: '880px' }}>
        {map[submoduleId] || <OverviewSection />}
      </div>
    </div>
  );
}
